import { defineComponent as _defineComponent } from 'vue'
import BCXBreadcrumbs from '@/components/molecules/BCXBreadcrumbs.vue';
import BCXContentArea from '@/components/molecules/BCXContentArea.vue';
import BCXIcon from '@/components/molecules/BCXIcon.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXUserBanner from '@/components/molecules/BCXUserBanner.vue';
import TagList from '@/components/tagging/TagList.vue';
import Voting from '@/components/voting/Voting.vue';
import animationFrameWait from '@/utils/animationFrameWait';
import BlogArticleToc, { type BlogArticleTocItem } from '@/views/blog/components/BlogArticleToc.vue';
import useI18n from '@/mixins/useI18n';
import useResponsiveness from '@/mixins/useResponsiveness';
import { RouterEntry } from '@/models/Router';
import { VoteReason } from '@/models/Voting';
import usePageState from '@/state/pageState';
import { BlogEntry } from '@/views/blog/models/Blog';
import {
  ComponentPublicInstance,
  computed, nextTick, onMounted, ref, watch
} from 'vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue2-helpers/vue-router';
import { markPrerenderReady } from '@/utils/prerenderReady';
import { updatePublicSeo } from '@/utils/publicSeo';
import { getBlogEntry, putUpdateBlogEntryView } from '../../../services/Blog/Api';
import { getLocalPublicBlogEntry } from '../../public/blog/localBlogData';
import { getPublicArticlePath, getPublicArticleSeoMeta } from '../../public/blog/publicArticleSeo';
import renderLocalPublicBlogMarkdown from '../../public/blog/renderLocalPublicBlogMarkdown';

type ArticleMode = 'authenticated' | 'preview' | 'public';

type HeadingScrollBehavior = 'auto' | 'smooth';


export default /*#__PURE__*/_defineComponent({
  __name: 'BlogArticle',
  props: {
    articleId: { default: '' },
    article: { default: undefined },
    mode: { default: 'authenticated' }
  },
  emits: ['hidePreview'],
  setup(__props: any, { emit }) {

const props = __props as { articleId: string, article: BlogEntry, mode: ArticleMode };





const { t } = useI18n();
const fetchedArticle = ref<BlogEntry>({} as BlogEntry);
const { isMobileLayout } = useResponsiveness();
const { pageNotFound } = usePageState();
const router = useRouter();
const route = useRoute();

const isAuthenticated = computed(() => props.mode === 'authenticated');
const isPreview = computed(() => props.mode === 'preview');
const isPublic = computed(() => props.mode === 'public');
const markdownMode = computed(() => (isPublic.value ? 'public-blog' : 'normal'));
const articleMarkdown = ref<ComponentPublicInstance | null>(null);
const tocItems = ref<BlogArticleTocItem[]>([]);

const articleData = computed<BlogEntry>(() => props.article ?? fetchedArticle.value);

const articleRoute = computed(() => {
  if (isPublic.value) {
    return { text: t('blog.breadcrumb').toString() || 'Blog', to: { name: 'blog-public' } };
  }
  switch (articleData.value?.type) {
    case 'BC_NEWS':
      return { text: t('blog.news').toString(), to: { name: 'blog news' } };
    case 'HELP_ARTICLE':
      return { text: t('blog.wiki').toString(), to: { name: 'wiki help' } };
    default:
      return { text: t('blog.articles').toString(), to: { name: 'blog articles' } };
  }
});

const articleOverviewRoute = computed(() => articleRoute.value.to);

const entryBreadcrumbItems = computed((): Array<RouterEntry> => {
  const breadcrumbItems: Array<RouterEntry> = [articleRoute.value, { text: articleData.value?.title }];
  if (!isPublic.value && isMobileLayout) {
    breadcrumbItems.unshift({ text: 'ByteCookie', to: { name: 'home' } });
  }
  return breadcrumbItems;
});

let assignTocHeadings = async () => Promise.resolve();

const updatePublicArticleSeo = (article: BlogEntry) => {
  if (!isPublic.value) return;

  updatePublicSeo(getPublicArticleSeoMeta(article));
};

const loadBlogEntry = async () => {
  if (props.article) {
    if (isPublic.value) {
      const canonicalPath = getPublicArticlePath(props.article);
      if (route.path !== canonicalPath) {
        router.replace({
          path: canonicalPath,
          query: route.query,
          hash: route.hash,
        });
        return;
      }
      updatePublicArticleSeo(props.article);
      await assignTocHeadings();
      markPrerenderReady(route.fullPath);
    }
    return;
  }

  let data: BlogEntry | undefined;

  if (isPublic.value) {
    // TODO: Replace with real public API call once the endpoint exists
    data = getLocalPublicBlogEntry(props.articleId);
  } else {
    data = await getBlogEntry(props.articleId);
  }

  if (!data) {
    const isWiki = route.path.includes('wiki');
    let notFoundRedirect = '/blog';

    if (isPublic.value) {
      notFoundRedirect = '/pblog';
    } else if (isWiki) {
      notFoundRedirect = '/wiki/help';
    }

    pageNotFound.value = true;
    router.push(notFoundRedirect);
    return;
  }

  if (isPublic.value) {
    const canonicalPath = getPublicArticlePath(data);
    if (route.path !== canonicalPath) {
      router.replace({
        path: canonicalPath,
        query: route.query,
        hash: route.hash,
      });
      return;
    }
  }

  fetchedArticle.value = data;
  updatePublicArticleSeo(data);
  if (isPublic.value) {
    await assignTocHeadings();
    markPrerenderReady(route.fullPath);
  }

  if (isAuthenticated.value) {
    putUpdateBlogEntryView(props.articleId);
  }
};

const articleContent = computed(() => {
  const content = articleData.value?.content || '';
  return isPublic.value ? renderLocalPublicBlogMarkdown(content) : content;
});

function slugifyHeading(text: string) {
  return text
    .toLowerCase()
    .replaceAll('ß', 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getHeadingScrollOffset() {
  return isMobileLayout.value ? 88 : 112;
}

function decodeHashFragment(hash: string) {
  const fragment = hash.replace(/^#/, '');

  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
}

function navigateToHeading(id: string, behavior: HeadingScrollBehavior = 'smooth') {
  const markdownRoot = articleMarkdown.value?.$el as HTMLElement | undefined;
  const target = document.getElementById(id);
  if (!target || !markdownRoot?.contains(target)) return;

  const top = window.scrollY + target.getBoundingClientRect().top - getHeadingScrollOffset();
  window.scrollTo({
    top: Math.max(top, 0),
    behavior,
  });
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${encodeURIComponent(id)}`);
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
};

assignTocHeadings = async () => {
  await nextTick();
  await animationFrameWait(2);

  const markdownRoot = articleMarkdown.value?.$el as HTMLElement | undefined;
  if (!markdownRoot) {
    tocItems.value = [];
    return;
  }

  const headings = [...markdownRoot.querySelectorAll<HTMLElement>('h1, h2, h3')];
  const slugCounts = new Map<string, number>();
  const presentLevels = [...new Set(
    headings
      .map((heading) => Number(heading.tagName.slice(1)))
      .filter((level): level is 1 | 2 | 3 => [1, 2, 3].includes(level))
  )].sort((a, b) => a - b);
  const normalizedLevelMap = new Map<number, 1 | 2 | 3>(
    presentLevels.map((level, index) => [level, (index + 1) as 1 | 2 | 3])
  );

  const nextTocItems = headings.reduce<BlogArticleTocItem[]>((items, heading, index) => {
    const text = heading.textContent?.trim() ?? '';
    const rawLevel = Number(heading.tagName.slice(1));

    if (!text || ![1, 2, 3].includes(rawLevel)) return items;

    const level = normalizedLevelMap.get(rawLevel);
    if (!level) return items;
    const baseSlug = slugifyHeading(text) || `section-${index + 1}`;
    const count = slugCounts.get(baseSlug) ?? 0;
    slugCounts.set(baseSlug, count + 1);
    const id = count ? `${baseSlug}-${count + 1}` : baseSlug;

    heading.id = id;
    heading.classList.add('article__anchor-heading');

    items.push({
      id,
      text,
      level,
    });
    return items;
  }, []);

  tocItems.value = nextTocItems;

  const hash = decodeHashFragment(route.hash);
  if (hash) {
    navigateToHeading(hash, 'auto');
  }
};

onMounted(loadBlogEntry);

watch(articleContent, () => {
  assignTocHeadings();
}, { immediate: true });

watch(() => route.hash, (hash) => {
  if (!hash) return;
  navigateToHeading(decodeHashFragment(hash), 'auto');
});

/**
 * Reload content when the user navigates between articles
 * without unmounting the component (same route, different `articleId` param).
 */
onBeforeRouteUpdate(async (to, from, next) => {
  next();
  await nextTick();
  loadBlogEntry();
});

return { __sfc: true,props, emit, t, fetchedArticle, isMobileLayout, pageNotFound, router, route, isAuthenticated, isPreview, isPublic, markdownMode, articleMarkdown, tocItems, articleData, articleRoute, articleOverviewRoute, entryBreadcrumbItems, assignTocHeadings, updatePublicArticleSeo, loadBlogEntry, articleContent, slugifyHeading, getHeadingScrollOffset, decodeHashFragment, navigateToHeading, scrollToTop, BCXBreadcrumbs, BCXContentArea, BCXIcon, BCXMarkdown, BCXUserBanner, TagList, Voting, BlogArticleToc, VoteReason }
}

})